<script lang='ts'>
	// Docs
	// 	 https://cookbook.openai.com/examples/how_to_call_functions_with_chat_models 
	// 

    import { AssistantStream } from 'openai/lib/AssistantStream'
	import { type AssistantStreamEvent } from 'openai/resources/beta/assistants'
    import { type RequiredActionFunctionToolCall } from 'openai/resources/beta/threads/runs/runs'
	import { onMount, tick } from 'svelte'
	import { writable } from 'svelte/store'

    import { BUTTON_TYPE } from '$lib/components/Common/Buttons/Button_types'
    import ButtonA from '$lib/components/Common/Buttons/ButtonA.svelte'
	import { account } from '$lib/stores/account/account'
    import { setAccount } from '$lib/stores/account/manager'
    import { openaiTokensAdd, openaiTokensAddDiff, saveOpenaiTokens } from '$lib/stores/openai/manager'
    import { openai_limit_reached_store } from '$lib/stores/openai/openai'
    import { makeOpenaiThreadItem } from '$lib/stores/openai_thread_item/factories'
    import { OpenaiThreadItemAppend, OpenaiThreadItemAppendToLastMessage } from '$lib/stores/openai_thread_items/manager'
    import { openai_thread_items_store } from '$lib/stores/openai_thread_items/openai_thread_items'

    import LoadingIcon from '../Common/Icons/LoadingIcon.svelte'
    import Input from '../Common/Inputs/Input.svelte'
	import OpenaiChatThreadItems from './OpenaiChatThreadItems.svelte'
    import OpenaiExamples from './OpenaiExamples.svelte'
	import { type ChatMessageProps } from './types'


	export let disabled = false
	export let functionCallHandler: (toolCall: RequiredActionFunctionToolCall) => Promise<string>
	export const focus = () => {handleFocus()}

	// const dispatch = createEventDispatcher()

	function handleFocus() {
		myInput.focus()
	}
	let myInput: Input

	// const userInput = writable('')
	let userInput = ''
	// const token_store = writable(0)
	const messages = writable<ChatMessageProps[]>([])
	const inputDisabled = writable(false)
	let threadId = ''


	let messagesEndRef: HTMLDivElement | null = null

	// Add these variables at the top of the script
	let messageBuffer = ''
	let updateTimeout: ReturnType<typeof setTimeout> | null = null
	const UPDATE_DELAY = 50 // milliseconds between updates

	async function scrollToBottom() {
		await tick()
		messagesEndRef?.scrollIntoView({ behavior: 'smooth' })
	}

	$: {
		if ($openai_thread_items_store) {
			scrollToBottom()
		}
	}

	onMount(async () => {
		await scrollToBottom()
		createThread()
	})

	async function createThread() {

		if ($account.openai_thread_id) {
			threadId = $account.openai_thread_id
			return
		}

		const res = await fetch(
			'/openai/api/assistants/threads', {
				method: 'POST',
			})
		const data = await res.json()
		threadId = data.threadId

		// threadId 
		setAccount({openai_thread_id: threadId})
	}

	async function handleSubmit(event: Event) {
		event.preventDefault()
		
		// $userInput = $userInput.trim()
		userInput = userInput.trim()
		if (!userInput) return
		
		const messageText = userInput
		// userInput.set('') // Clear input immediately\
		userInput = ''
		inputDisabled.set(true)
		
		// Create and append the user message first
		let openaiThreadItem = makeOpenaiThreadItem({})
		openaiThreadItem.thread_id = $account.openai_thread_id
		openaiThreadItem.question = messageText
		OpenaiThreadItemAppend(openaiThreadItem)
		
		// Update tokens
		openaiTokensAdd(messageText)
		
		// Use setTimeout to prevent UI blocking
		setTimeout(async () => {
			try {
				await sendMessage(messageText)
			} catch (error) {
				inputDisabled.set(false)
			}
		}, 0)
		
		scrollToBottom()
	}

	async function sendMessage(text: string) {
		const response = await fetch(
			`/openai/api/assistants/threads/${threadId}/messages`,
			{
				method: 'POST',
				body: JSON.stringify({ content: text }),
			}
		)
		if (!response.ok) {
			console.error('#1437 sendMessage response', response)
			if (response.status === 429) {
				console.log('Limit reached:', response.status)
				$openai_limit_reached_store = true
			}
			throw new Error(`HTTP error! status: ${response.status}`)
		}

		if (response.body) {
			const stream = AssistantStream.fromReadableStream(response.body)
			handleReadableStream(stream)
		}
	}

	const handleReadableStream = (stream: AssistantStream) => {
		const processEvent = (handler: Function, ...args: any[]) => {
			setTimeout(() => {
				handler(...args)
			}, 0)
		}

		stream.on('textCreated', () => processEvent(handleTextCreated))
		stream.on('textDelta', (delta) => {
			if (delta.value != null) {
				// Buffer the text and schedule an update
				messageBuffer += delta.value
				scheduleUpdate()
			}
			if (delta.annotations != null) {
				processEvent(() => annotateLastMessage(delta.annotations))
			}
		})
		stream.on('imageFileDone', (image) => processEvent(handleImageFileDone, image))
		stream.on('toolCallCreated', (toolCall) => processEvent(toolCallCreated, toolCall))
		stream.on('toolCallDelta', (delta, snapshot) => processEvent(toolCallDelta, delta, snapshot))

		stream.on('event', (event) => {
			if (event.event === 'thread.run.requires_action') {
				processEvent(() => handleRequiresAction(event))
			}
			if (event.event === 'thread.run.completed') {
				// Flush any remaining buffer
				if (messageBuffer) {
					flushMessageBuffer()
				}
				processEvent(() => handleRunCompleted())
			}
		})
	}

	function scheduleUpdate() {
		if (updateTimeout) {
			clearTimeout(updateTimeout)
		}
		
		updateTimeout = setTimeout(() => {
			flushMessageBuffer()
		}, UPDATE_DELAY)
	}

	function flushMessageBuffer() {
		if (messageBuffer) {
			requestAnimationFrame(() => {
				OpenaiThreadItemAppendToLastMessage(messageBuffer)
				openaiTokensAdd(messageBuffer)
				messageBuffer = ''
				scrollToBottom()
			})
		}
	}

	// Update appendToLastMessage to use the buffer system
	function appendToLastMessage(text: string) {
		messageBuffer += text
		scheduleUpdate()
	}

	/* Stream Event Handlers */
	// textCreated - create new assistant message
	const handleTextCreated = () => {
		appendMessage('assistant', '')
	}

	// textDelta - append text to last assistant message
	const handleTextDelta = (delta: any) => {
		if (delta.value != null) {
			appendToLastMessage(delta.value)
		}
		if (delta.annotations != null) {
			annotateLastMessage(delta.annotations)
		}
	}

	// imageFileDone - show image in chat
	const handleImageFileDone = (image: { file_id: string }) => {
		appendToLastMessage(`\n![${image.file_id}](/api/files/${image.file_id})\n`)
	}

	// toolCallCreated - log new tool call
	const toolCallCreated = (toolCall: { type: string }) => {
		if (toolCall.type != 'code_interpreter') return
		appendMessage('code', '')
	}

	// toolCallDelta - log delta and snapshot for the tool call
	const toolCallDelta = (delta: any, snapshot: any) => {
		if (delta.type !== 'code_interpreter') return
		if (!delta.code_interpreter?.input) return
		appendToLastMessage(delta.code_interpreter.input)
	}

	// handleRequiresAction - handle function call
	const handleRequiresAction = async (
		event: AssistantStreamEvent.ThreadRunRequiresAction
	) => {
		const runId = event.data.id
		const toolCalls = event.data.required_action?.submit_tool_outputs.tool_calls

		// loop over tool calls and call function handler
		if (toolCalls) {
			const toolCallOutputs = await Promise.all(
				toolCalls.map(async (toolCall: RequiredActionFunctionToolCall) => {
					const result = await functionCallHandler(toolCall)
					return { output: result, tool_call_id: toolCall.id }
				})
			)
			submitActionResult(runId, toolCallOutputs)
		}
		inputDisabled.set(true)
	}

	function handleRunCompleted() {
	  inputDisabled.set(false)
	  saveOpenaiTokens()
	}

	async function submitActionResult(runId: string, toolCallOutputs: any) {
		const response = await fetch(
			`/openai/api/assistants/threads/${threadId}/actions`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					runId,
					toolCallOutputs,
				}),
			}
		)
		// tokens upsert
		openaiTokensAdd(toolCallOutputs.map((output: any) => output.output).join('\n'))

		if (response.body) {
			const stream = AssistantStream.fromReadableStream(response.body)
			handleReadableStream(stream)
		}	  
	}

	function appendMessage(role: string, text: string) {
		let openaiThreadItem = makeOpenaiThreadItem({})
		openaiThreadItem.thread_id = $account.openai_thread_id
		if (role === 'user') {
			openaiThreadItem.question = text
			OpenaiThreadItemAppend(openaiThreadItem)
		} else {
			openaiThreadItem.response = text
			OpenaiThreadItemAppendToLastMessage(text)
		}	

		
		// tokens upsert
		openaiTokensAdd(text)
	}

	function annotateLastMessage(annotations: any[]) {
		messages.update((prev) => {
			const lastMessage = prev[prev.length - 1]
			if (!lastMessage) return prev

			const originalText = lastMessage.text

			annotations.forEach((annotation) => {
				if (annotation.type === 'file_path') {
					lastMessage.text = lastMessage.text.replace(
						new RegExp(annotation.text, 'g'),
						`/api/files/${annotation.file_path.file_id}`
					)
				}
			})

			const updatedText = lastMessage.text

			// tokens upsert
			openaiTokensAddDiff(originalText, updatedText)

			return [...prev.slice(0, -1), lastMessage]
		})
	}



	function handleUseExample(e: CustomEvent): void {
		userInput = e.detail
		handleSubmit(new Event('submit'))
	}
</script>
  

<div class="chatContainer">

	<OpenaiChatThreadItems />
	<div class="promptSection">
		{#if $inputDisabled}
			<div class="thinking">
				<LoadingIcon size={4} color={'green'} loading={true}/>
				&nbsp;&nbsp; thinking &nbsp;<span class="cursor">. . .</span>
			</div>
		{/if}
		<form on:submit={handleSubmit} class="inputForm gap-2">
			<Input
				bind:this={myInput}
				data-test-id="openai_chat_input"
				type="input"
				value={userInput}
				placeholder="Enter your question"
				disabled={disabled ||$inputDisabled}
				on:change={(e) => {userInput = e.detail}}
			/>
			<ButtonA html_type="submit" 
				class="button" 
				data-test-id="openai_chat_send_button"
				type={BUTTON_TYPE.PRIMARY}
				disabled={disabled ||$inputDisabled}>
				Submit
			</ButtonA>
		</form>

		<OpenaiExamples 
			on:switchView={()=> scrollToBottom()}
			on:useExample={handleUseExample}
		/>

		<div bind:this={messagesEndRef}></div>
	</div>
</div>
  
<style lang="postcss">

	.chatContainer {
	  display: flex;
	  flex-direction: column;
	  height: 100%;
	  width: 100%;
	}
  
	.promptSection {
		width: 100%;
		padding: 10px;
		padding-bottom: 20px;
		order: 2;
	}

	.thinking
	{
		@apply flex text-gray-600;
		width: 100%;
		padding-left: 20px;
		padding-bottom: 10px;
	}

	.cursor {
		animation: blink 1s infinite;
	}
	@keyframes blink {
		50% {
			color: transparent;
		}
	}

	.inputForm {
		display: flex;
	}


</style>
